import { fetchSettingsCollection, SettingItem } from './settingsService'

export interface Country {
  id: number
  name: string
  code: string
  currency: string
}

export interface CountryConfig {
  country: Country
  currency: string
  phoneRegion: string
}

/**
 * Fallback used only when Settings API omits/fails country+currency.
 * Preserves pre-existing cashier behavior (SAR icon / SA phone region).
 * Brand/flavor must never set this — Settings is the source of truth.
 */
export const DEFAULT_COUNTRY_CONFIG: CountryConfig = {
  country: {
    id: 0,
    name: '',
    code: '',
    currency: '',
  },
  currency: '',
  phoneRegion: '',
}

type UnknownRecord = Record<string, unknown>

const parsePossibleJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value

  const trimmed = value.trim()
  if (!trimmed) return null
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return trimmed

  try {
    return JSON.parse(trimmed) as unknown
  } catch {
    return trimmed
  }
}

const getObjectString = (
  value: unknown,
  keys: string[]
): string | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const record = value as UnknownRecord
  for (const key of keys) {
    const candidate = record[key]
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }

  return undefined
}

const getObjectNumber = (
  value: unknown,
  keys: string[]
): number | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const record = value as UnknownRecord
  for (const key of keys) {
    const candidate = Number(record[key])
    if (Number.isInteger(candidate) && candidate > 0) return candidate
  }

  return undefined
}

/** Normalize Settings country value → ISO-2 when recognizable; otherwise pass through uppercased code/name. */
const resolveCountryCode = (raw: string): string => {
  const trimmed = raw.trim()
  if (!trimmed) return ''

  const upper = trimmed.toUpperCase()
  if (/^[A-Z]{2}$/.test(upper)) return upper

  const normalized = trimmed.toLocaleLowerCase('en').replace(/\s+/g, ' ')
  // Name aliases only help parse Settings payloads — they do not select the active country.
  if (
    normalized === 'saudi arabia' ||
    normalized === 'kingdom of saudi arabia' ||
    normalized === 'ksa' ||
    normalized === 'السعودية' ||
    normalized === 'المملكة العربية السعودية'
  ) {
    return 'SA'
  }
  if (normalized === 'egypt' || normalized === 'مصر') {
    return 'EG'
  }

  return upper.length === 2 ? upper : trimmed
}

const normalizeCurrency = (value: unknown): string => {
  const parsed = parsePossibleJson(value)
  const candidate =
    typeof parsed === 'string'
      ? parsed
      : getObjectString(parsed, [
          'currency',
          'currencyCode',
          'currency_code',
          'code',
          'iso',
        ])
  const currency = candidate?.trim().toUpperCase() ?? ''
  return /^[A-Z]{3}$/.test(currency) ? currency : currency
}

/**
 * Build CountryConfig strictly from Settings collection keys `country` and `currency`.
 */
export function extractCountryConfig(collection: SettingItem[]): CountryConfig {
  const countrySetting = collection.find((item) => item.key === 'country')
  const currencySetting = collection.find((item) => item.key === 'currency')

  if (!countrySetting?.value && !currencySetting?.value) {
    return DEFAULT_COUNTRY_CONFIG
  }

  const parsedCountry = parsePossibleJson(countrySetting?.value)

  const configuredName =
    typeof parsedCountry === 'string'
      ? parsedCountry
      : getObjectString(parsedCountry, ['name', 'countryName', 'country_name'])
  const configuredCode =
    typeof parsedCountry === 'string'
      ? parsedCountry
      : getObjectString(parsedCountry, [
          'code',
          'iso',
          'iso2',
          'countryCode',
          'country_code',
        ])

  const code = resolveCountryCode(configuredCode || configuredName || '')
  const name =
    (configuredName && configuredName.trim()) ||
    (typeof parsedCountry === 'string' ? parsedCountry.trim() : '') ||
    code

  const countryCurrency = getObjectString(parsedCountry, [
    'currency',
    'currencyCode',
    'currency_code',
  ])
  const currency =
    normalizeCurrency(currencySetting?.value) ||
    normalizeCurrency(countryCurrency) ||
    ''

  return {
    country: {
      id:
        getObjectNumber(parsedCountry, ['id', 'countryId', 'country_id']) || 0,
      name,
      code,
      currency,
    },
    currency,
    phoneRegion: code,
  }
}

let cachedConfig: CountryConfig = DEFAULT_COUNTRY_CONFIG
let loadPromise: Promise<CountryConfig> | null = null
const listeners = new Set<(config: CountryConfig) => void>()

function notifyListeners(config: CountryConfig) {
  listeners.forEach((listener) => listener(config))
}

export function getCountryConfig(): CountryConfig {
  return cachedConfig
}

export function subscribeCountryConfig(
  listener: (config: CountryConfig) => void
): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export async function loadCountryConfig(): Promise<CountryConfig> {
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const collection = await fetchSettingsCollection()
        const config = extractCountryConfig(collection)
        cachedConfig = config
        notifyListeners(config)
        window.dispatchEvent(
          new CustomEvent('countryConfigApplied', { detail: config })
        )
        return config
      } catch {
        cachedConfig = DEFAULT_COUNTRY_CONFIG
        return DEFAULT_COUNTRY_CONFIG
      }
    })()
  }

  return loadPromise
}

export async function initCountryConfig(): Promise<void> {
  try {
    await loadCountryConfig()
  } catch {
    cachedConfig = DEFAULT_COUNTRY_CONFIG
  }
}
